// 개발요청 상세 — 기타 첨부(D4b · 2판 V2 제어형): 칸이 없는 첨부 목록 · 올리기(하나씩 차례로) · 내려받기(blob → a[download]) · 지우기.
// 🔴 목록의 주인은 상세다(DevRequestDetail 이 files 를 한 번 불러 칸별 첨부와 이 묶음에 나눠 준다) — 여기서 다시 부르지 않는다.
//    올리기·지우기가 성공하면 onFilesChanged() 로 상세에 알린다(상세가 목록을 다시 받는다).
// 🔴 단추는 서버 값으로만 — 올리기 = item.can.attach(canAttach 로 받는다) · 지우기 = 그 행의 can_delete.
// 🔴 상한·허용 확장자는 서버 services/devreq_files.py 의 거울이다(DevRequestAttachField 의 상수 · 상한은 칸 첨부까지 합친 totalCount).
//    100MB 초과는 보내기 전에 화면에서 거른다 — nginx 본문 한도에 걸리면 JSON 없는 413 이라 서버 문구가 닿지 않는다.
// 비공개라 못 보는 사람에게는 이 묶음 자체가 없다(상세가 masked 면 그리지 않는다).
import { useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, btn } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';
import { ACCEPT, ALLOWED_EXT_GROUPS, MAX_FILES, MAX_FILE_BYTES, MAX_FILE_MB, TOO_BIG, saveDevreqFile } from './DevRequestAttachField';
import { errorText, text } from './devreqLabels';

const UPLOAD_FAILED = '파일을 올리지 못했습니다.';
const DOWNLOAD_FAILED = '파일을 내려받지 못했습니다.';
const DELETE_FAILED = '파일을 지우지 못했습니다.';

const formatBytes = (value) => {
  const n = Number(value) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const subText = { fontSize: '11px', color: COLORS.textSub, whiteSpace: 'nowrap' };
const nameBtn = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit',
  color: COLORS.primaryDark, fontSize: '13px', textAlign: 'left', flex: 1, minWidth: 0, overflowWrap: 'anywhere',
};

// files = 칸이 없는 첨부(null = 불러오는 중) · totalCount = 요청 전체의 살아 있는 첨부 수(상한 검사용).
export default function DevRequestFiles({
  requestId, canAttach = false, files = null, totalCount = 0, loadError = '', onFilesChanged,
}) {
  const [failed, setFailed] = useState([]);   // [{name, message}] — 올리지 못한 파일만
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const aliveRef = useRef(true);
  const inputRef = useRef(null);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // 🔴 인자는 이미 배열이다(입력칸 value 를 비우기 전에 복사해 넘긴다). 하나씩 차례로 보낸다.
  const uploadFiles = async (list) => {
    if (!canAttach || busyRef.current || files === null || list.length === 0) return;
    const problems = [];
    const queue = [];
    list.forEach((file) => {
      if (file.size > MAX_FILE_BYTES) problems.push({ name: file.name, message: TOO_BIG });
      else queue.push(file);
    });
    busyRef.current = true;
    setBusy(true);
    setError('');
    let added = 0;
    try {
      for (const file of queue) {
        if (!aliveRef.current) break;
        const formData = new FormData();
        formData.append('file', file);
        try {
          await devreqAPI.upload(requestId, formData);
          if (!aliveRef.current) break;
          added += 1;
        } catch (err) {
          // 실패한 파일만 이름과 서버 문구로 남기고 다음 파일을 계속 보낸다.
          problems.push({ name: file.name, message: await errorText(err, UPLOAD_FAILED) });
        }
      }
    } finally {
      busyRef.current = false;
      if (aliveRef.current) {
        setBusy(false);
        setFailed(problems);
      }
    }
    if (added && aliveRef.current) onFilesChanged?.();
  };

  const pickFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';   // 같은 파일을 다시 고를 수 있게(복사 뒤에 비운다)
    uploadFiles(picked);
  };

  const download = async (file) => {
    setError('');
    try {
      await saveDevreqFile(file);
    } catch (err) {
      const message = await errorText(err, DOWNLOAD_FAILED);
      if (aliveRef.current) setError(message);
    }
  };

  const remove = async (file) => {
    if (!(await confirmDialog(`${file.name} 파일을 지울까요?`))) return;
    setError('');
    try {
      await devreqAPI.removeFile(file.id);
      if (!aliveRef.current) return;
      onFilesChanged?.();
    } catch (err) {
      const message = await errorText(err, DELETE_FAILED);
      if (aliveRef.current) setError(message);
    }
  };

  const items = Array.isArray(files) ? files : null;
  const count = items ? items.length : 0;
  const pickDisabled = busy || items === null || totalCount >= MAX_FILES;

  return (
    <section aria-label="첨부" style={{ border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '10px 14px', marginBottom: '12px', background: COLORS.surface }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700 }}>{`기타 첨부 (${count})`}</h3>
        {canAttach && (
          <>
            <button type="button" onClick={() => inputRef.current?.click()} disabled={pickDisabled}
              style={{ ...btn('secondary', 'sm'), marginLeft: 'auto', opacity: pickDisabled ? 0.5 : 1 }}>
              {busy ? '올리는 중…' : '첨부 올리기'}
            </button>
            <input type="file" multiple accept={ACCEPT} aria-label="올릴 파일" hidden ref={inputRef} onChange={pickFiles} />
          </>
        )}
      </div>
      {canAttach && (
        <div style={{ margin: '0 0 6px', fontSize: '11px', color: COLORS.textSub, overflowWrap: 'anywhere' }}>
          <p style={{ margin: 0 }}>{`올릴 수 있는 파일 · 파일당 ${MAX_FILE_MB}MB · 요청당 ${MAX_FILES}개`}</p>
          <ul style={{ listStyle: 'none', margin: '2px 0 0', padding: 0 }}>
            {ALLOWED_EXT_GROUPS.map((g) => (
              <li key={g.label}>{`${g.label}: ${g.exts.join('\u00a0· ')}`}</li>
            ))}
          </ul>
        </div>
      )}

      {loadError && <p role="alert" style={{ margin: 0, fontSize: '12px', color: COLORS.dangerText }}>{loadError}</p>}
      {!loadError && items === null && <p role="status" style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</p>}
      {items && items.length === 0 && <p style={{ margin: 0, fontSize: '12px', color: COLORS.textSub }}>기타 첨부가 없습니다</p>}
      {items && items.length > 0 && (
        <ul aria-label="첨부 목록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((file) => (
            <li key={file.id} data-testid="devreq-file-row"
              style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', padding: '6px 0', borderTop: `1px solid ${COLORS.borderSoft}` }}>
              <button type="button" aria-label={`${file.name} 내려받기`} onClick={() => download(file)} style={nameBtn}>{text(file.name)}</button>
              <span style={subText}>{formatBytes(file.size)}</span>
              <span style={subText}>{text(file.uploaded_by_name)}</span>
              <span style={subText}>{fmtKstDateTime(file.created_at) || '—'}</span>
              {file.can_delete === true && (
                <button type="button" aria-label={`${file.name} 지우기`} onClick={() => remove(file)} style={btn('secondary', 'sm')}>지우기</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {failed.length > 0 && (
        <ul aria-label="올리지 못한 파일" style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: '12px', color: COLORS.dangerText }}>
          {failed.map((f, i) => <li key={`${f.name}-${i}`} style={{ overflowWrap: 'anywhere' }}>{`${f.name}: ${f.message}`}</li>)}
        </ul>
      )}
      {error && <p role="alert" style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.dangerText }}>{error}</p>}
    </section>
  );
}
